import ensureServerInterview from '@Application/Beneficiaries/Interviews/ensureServerInterview';
import fetchLiveTranscriptionAvailability from '@Application/Beneficiaries/Interviews/LiveTranscription/fetchLiveTranscriptionAvailability';
import openLiveTranscription, {
	type LiveTranscription,
	type LiveTranscriptionTarget,
} from '@Application/Beneficiaries/Interviews/LiveTranscription/openLiveTranscription';
import FetchLiveTranscriptionAvailability from '@Application/Beneficiaries/Interviews/LiveTranscription/Queries/FetchLiveTranscriptionAvailability';
import { InterviewKind } from '@Domain/Enums/Interviews/InterviewKind';
import type { CaptureChunk } from '@Domain/Ports/Capture/CapturePort';
import type { LocalInterview } from '@Domain/Ports/LocalInterviews/LocalInterviewPort';
import useConnectivity from '@UI/Providers/PortsProvider/Hooks/Connectivity/useConnectivity';
import useLocalInterviewPort from '@UI/Providers/PortsProvider/Hooks/LocalInterviews/useLocalInterviewPort';
import { useDispatch } from '@verclam/synapse';
import * as React from 'react';

type LiveTranscriptionControls = {
	/** Arms the live transcription for this interview; nothing is requested until the first fragment. */
	begin: (localInterview: LocalInterview) => void;
	forward: (chunk: CaptureChunk) => void;
	/** Ends the live session without making the caller wait for it. */
	end: () => void;
};

/**
 * Feeds the fragments of a capture to the live transcription. Best effort and silent: offline at
 * `begin`, an unavailable feature or any failure only means no live transcription, never a message.
 */
const useLiveTranscription = (): LiveTranscriptionControls => {
	const dispatch = useDispatch();
	const isOnline = useConnectivity();
	const localInterviewPort = useLocalInterviewPort();

	const pendingInterviewRef = React.useRef<LocalInterview | null>(null);
	const liveRef = React.useRef<LiveTranscription | null>(null);

	const resolveTarget = React.useCallback(
		async (localInterview: LocalInterview): Promise<LiveTranscriptionTarget | null> => {
			try {
				const availability = await dispatch(
					fetchLiveTranscriptionAvailability(
						new FetchLiveTranscriptionAvailability(
							localInterview.facilityId,
							localInterview.kind ?? InterviewKind.CONVERSATION,
						),
					),
				);

				if (
					availability.type !== 'LIVE_TRANSCRIPTION_AVAILABILITY_FETCHED' ||
					!availability.payload.available
				) {
					return null;
				}

				const identity = await ensureServerInterview(dispatch, localInterviewPort, localInterview);

				if (identity.type !== 'ready') {
					return null;
				}

				return {
					facilityId: localInterview.facilityId,
					subject: localInterview.subject,
					interviewId: identity.interviewId,
					recordingId: identity.recordingId,
				};
			} catch {
				return null;
			}
		},
		[dispatch, localInterviewPort],
	);

	const begin = React.useCallback(
		(localInterview: LocalInterview) => {
			if (isOnline) {
				pendingInterviewRef.current = localInterview;
			}
		},
		[isOnline],
	);

	const forward = React.useCallback(
		(chunk: CaptureChunk) => {
			if (liveRef.current === null) {
				const localInterview = pendingInterviewRef.current;

				if (localInterview === null) {
					return;
				}

				pendingInterviewRef.current = null;
				liveRef.current = openLiveTranscription(resolveTarget(localInterview));
			}

			liveRef.current.forward(chunk);
		},
		[resolveTarget],
	);

	const end = React.useCallback(() => {
		pendingInterviewRef.current = null;
		void liveRef.current?.close();
		liveRef.current = null;
	}, []);

	React.useEffect(() => end, [end]);

	return React.useMemo(() => ({ begin, forward, end }), [begin, forward, end]);
};

export default useLiveTranscription;
